import { getJoinUrl } from "@/helpers/joinUrl";
import { useGameStore } from "@/store/gameStore";
import { Check, LoaderCircle, RotateCw, Share } from "lucide-react";
import React, { useState } from "react";
import { renderSVG } from "uqr";
import { useShareGame } from "./menuComponents";
import {
  CircleButton,
  ConfirmDialog,
  PanelHeader,
  PillButton,
} from "./panelControls";

/**
 * The QR code and link other players use to draw from this game, shown when
 * the card grows. Opening it shares the game first if it's still on this
 * device alone.
 */
export const ShareFace: React.FC<{
  share: ReturnType<typeof useShareGame>;
  close: () => void;
}> = ({ share, close }) => {
  const slug = useGameStore((state) => state.game.slug);
  const joinUrl = slug ? getJoinUrl(slug) : null;
  const [confirmingNewCode, setConfirmingNewCode] = useState(false);

  return (
    <div className="flex flex-col items-center gap-5 p-4 text-white">
      <div className="flex w-full flex-col gap-2">
        <PanelHeader title="Scan to Join" closeLabel="Close" onClose={close}>
          <CircleButton
            label="New share code"
            disabled={!slug || share.isLoading}
            onClick={() => setConfirmingNewCode(true)}
          >
            <RotateCw className="size-6" strokeWidth={2.75} />
          </CircleButton>
        </PanelHeader>
        <p className="text-center text-white/60">
          Draw cards from multiple devices
        </p>
      </div>

      {/* The code's space, kept while sharing so the card doesn't resize
          when the code arrives. */}
      <div className="grid size-60 place-items-center">
        {joinUrl && !share.isLoading ? (
          <QrCode url={joinUrl} />
        ) : share.error ? (
          <div className="flex flex-col items-center gap-3">
            <p>{share.error}</p>
            <PillButton
              className="min-h-12 px-6 text-lg"
              onClick={share.handleEnableSync}
            >
              Try Again
            </PillButton>
          </div>
        ) : (
          <LoaderCircle
            className="size-10 animate-spin text-white/70"
            aria-label="Sharing"
          />
        )}
      </div>

      <div className="flex w-full flex-col gap-3">
        {/* The link written out, for reading to someone nearby. */}
        {joinUrl ? <JoinLink url={joinUrl} /> : <p>&nbsp;</p>}
        <PillButton
          tinted={Boolean(joinUrl)}
          disabled={!joinUrl}
          onClick={share.handleShareUrl}
        >
          {share.copied ? (
            <Check className="size-6" strokeWidth={3} aria-hidden />
          ) : (
            <Share className="size-6" strokeWidth={3} aria-hidden />
          )}
          {share.copied ? "Copied" : "Share Link"}
        </PillButton>
      </div>

      <ConfirmDialog
        open={confirmingNewCode}
        title="New Share Code?"
        message="This game moves to a new code with its cards as they are. Players using the current code stay on the old game."
        confirm="New Code"
        onConfirm={() => {
          setConfirmingNewCode(false);
          share.handleReshare();
        }}
        onCancel={() => setConfirmingNewCode(false)}
      />
    </div>
  );
};

/** `url` as a QR code on a white card. */
const QrCode: React.FC<{ url: string }> = ({ url }) => {
  const svg = React.useMemo(
    () => renderSVG(url, { border: 2, ecc: "M" }),
    [url],
  );

  return (
    <div
      role="img"
      aria-label="Join game QR code"
      className="size-full overflow-hidden rounded-[20px] bg-white p-3 [&_svg]:size-full"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

// Wraps only before the game code, never inside it.
const JoinLink: React.FC<{ url: string }> = ({ url }) => {
  const link = url.replace(/^https?:\/\//, "");
  const codeStart = link.lastIndexOf("/") + 1;

  return (
    <p className="text-center text-[17px] font-semibold text-white/60 select-text">
      {link.slice(0, codeStart)}
      <wbr />
      <span className="whitespace-nowrap">{link.slice(codeStart)}</span>
    </p>
  );
};
